import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from '../../utils/api';
import { FaTrash, FaEye, FaFilePdf, FaSearch, FaFilter } from 'react-icons/fa';
import './QuotationHistory.css';
import { COMPANY_LOGOS, generateQuotationPDF } from '../../utils/generateQuotationPdf';

const QuotationHistory = () => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    insuranceCompany: '',
    startDate: '',
    endDate: '',
    sortBy: 'timestamp',
    sortOrder: 'desc'
  });

  useEffect(() => {
    fetchQuotations();
  }, [filters]);

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.insuranceCompany) params.append('insuranceCompany', filters.insuranceCompany);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      params.append('sortBy', filters.sortBy);
      params.append('sortOrder', filters.sortOrder);

      const response = await axios.get(`/quotation/all?${params.toString()}`);
      setQuotations(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch quotations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this quotation?')) return;

    try {
      await axios.delete(`/quotation/delete/${id}`);
      toast.success('Quotation deleted successfully');
      fetchQuotations();
    } catch (error) {
      toast.error('Failed to delete quotation');
    }
  };

  const handleView = async (id) => {
  try {
    const response = await axios.get(`/quotation/${id}`);
    const data = response.data.data || {};

    setSelectedQuotation({
      ...data,
      ...data.finalPDFData,

      premiumDetails: {
        ...(data.premiumDetails || {}),
        ...(data.finalPDFData?.premiumDetails || {})
      },

      premiumBreakdown: {
        ...(data.premiumBreakdown || {}),
        ...(data.finalPDFData?.premiumBreakdown || {})
      },

      discounts: {
        ...(data.discounts || {}),
        ...(data.finalPDFData?.discounts || {})
      },

      taxes: {
        ...(data.taxes || {}),
        ...(data.finalPDFData?.taxes || {})
      },

      liability: {
        ...(data.liability || {}),
        ...(data.finalPDFData?.liability || {})
      }
    });

  } catch (error) {
    toast.error("Failed to fetch quotation details");
  }
}; 

  const handleExportPDF = async (quotation) => {
    if (!quotation) return;

    const base = quotation.finalPDFData || quotation;

    const data = {
      ...base,

      paOwnerDriver: base.paOwnerDriver || 0,
      paUnnamedPassenger: base.paUnnamedPassenger || 0,
      paPaidDriver: base.paPaidDriver || 0,
      llPaidDriver: base.llPaidDriver || 0,
      llEmployees: base.llEmployees || 0,

      addOns: base.addOns || base.addonDetails || {},

      addon_amount: base.addon_amount || 0,
      net_liability_B: base.net_liability_B || 0,

      premiumDetails: base.premiumDetails || {},
      premiumBreakdown: base.premiumBreakdown || {},
      discounts: base.discounts || {},
      taxes: base.taxes || {},
      vehicleDetails: base.vehicleDetails || {},
      quoteMeta: base.quoteMeta || {},
    };

    try {
      await generateQuotationPDF(data, { companyLogos: COMPANY_LOGOS });
      toast.success('Premium PDF Exported!');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-IN');
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="quotation-history">

      {/* HEADER */}
      <div className="admin-header">
        <div className="container">
          <h1>Quotation History</h1>
          <p>View and manage all generated quotations</p>
        </div>
      </div>

      <div className="admin-content">
        <div className="container">

          {/* FILTERS */}
          <div className="filters-section card">
            <div className="filters-header">
              <FaFilter /> Filters
            </div>
            <div className="filters-grid">
              
              {/* SEARCH */}
              <div className="filter-group">
                <label>Search</label>
                <input
                  type="text"
                  name="search"
                  placeholder="Search customer name, mobile..."
                  value={filters.search}
                  onChange={handleFilterChange}
                  className="form-input"
                />
              </div>

              {/* COMPANY */}
              <div className="filter-group">
                <label>Insurance Company</label>
                <select
                  name="insuranceCompany"
                  value={filters.insuranceCompany}
                  onChange={handleFilterChange}
                  className="form-select"
                >
                  <option value="">All</option>
                  {Object.keys(COMPANY_LOGOS).map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>

              {/* DATES */}
              <div className="filter-group">
                <label>Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className="form-input"
                />
              </div>

              <div className="filter-group">
                <label>End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  className="form-input"
                />
              </div>

            </div>
          </div>

          {/* COUNT */}
          <div className="summary-stats">
            <div className="stat-badge">
              Total: <strong>{quotations.length}</strong> quotations
            </div>
          </div>

          {/* LIST VIEW */}
          {loading ? (
            <div className="spinner"></div>
          ) : (
            <div className="quotations-grid">

              {quotations.map((q) => {
                const data = q.finalPDFData || q;
                return (
                <div key={q._id} className="quotation-card card">

                  <div className="quotation-header">
                    <div>
                      <h3
                        className="q-name"
                        style={{ whiteSpace: "normal", wordBreak: "break-word" }}
                      >
                        {data.customerName}
                      </h3>
                      <p className="q-company">{q.insuranceCompany}</p>
                    </div>

                    <div className="quotation-actions">
                      <button onClick={() => handleView(q._id)} className="icon-btn"><FaEye /></button>
                      <button onClick={() => handleExportPDF(q)} className="icon-btn"><FaFilePdf /></button>
                      <button onClick={() => handleDelete(q._id)} className="icon-btn delete"><FaTrash /></button>
                    </div>
                  </div>

                  <div className="detail-row">
                    <span>Reg No:</span>
                    <strong>{data.vehicleDetails?.registrationNumber}</strong>
                  </div>

                  <div className="detail-row">
                    <span>Vehicle:</span>
                    <span>{data.vehicleDetails?.make} {data.vehicleDetails?.model}</span>
                  </div>

                  <div className="detail-row premium-highlight">
                    <span>Total Premium:</span>
                    <strong className="premium-amount">
                      ₹{data.premiumDetails?.grossPremium?.toLocaleString('en-IN') || 0}
                    </strong>
                  </div>

                  <div className="detail-row">
                    <span>Date:</span>
                    <span>{formatDate(q.timestamp)}</span>
                  </div>
                </div>
              )})}

            </div>
          )}

          {/* FULL MODAL UI */}
          {selectedQuotation && (
            <div className="modal-overlay" onClick={() => setSelectedQuotation(null)}>
              <div className="modal-content card" onClick={(e) => e.stopPropagation()}>
                
                <div className="modal-header">
                  <h2>Quotation Details</h2>
                  <button onClick={() => setSelectedQuotation(null)} className="close-btn">×</button>
                </div>

                <div className="modal-body">
                  {(() => {
                    const q = selectedQuotation;
                    const addOnLabels = {
                      zeroDepreciation: "Zero Depreciation",
                      engineProtect: "Engine Protection",
                      rti: "Return To Invoice (RTI)",
                      consumables: "Consumable",
                      tyreProtect: "Tyre Protection",
                      keyProtect: "Key Protection",
                      rodentDamage: "Rodent-bite",
                      personalBelongings: "Personal Belonging",
                      emergencyTransport: "Emergency Transport",
                      emergencyHotelExpenses: "Hotel Expenses",
                      roadSideAssistance: "Road Side Assistance"
                    };

                    return (
                      <>
                        {/* CUSTOMER INFO */}
                        <div className="detail-section">
                          <h3>Customer Information</h3>
                          <div className="detail-grid">
                            <p><strong>Name:</strong> {q.customerName}</p>
                            <p><strong>Email:</strong> {q.customerEmail || "-"}</p>
                            <p><strong>Mobile:</strong> {q.customerMobile}</p>
                            <p><strong>Company:</strong> {q.insuranceCompany}</p>
                          </div>
                        </div>

                        {/* VEHICLE DETAILS */}
                        <div className="detail-section">
                          <h3>Vehicle Details</h3>
                          <div className="detail-grid">
                            <p><strong>Registration No:</strong> {q.vehicleDetails?.registrationNumber}</p>
                            <p><strong>Make:</strong> {q.vehicleDetails?.make}</p>
                            <p><strong>Model:</strong> {q.vehicleDetails?.model}</p>
                            <p><strong>Variant:</strong> {q.vehicleDetails?.variant}</p>
                            <p><strong>Fuel Type:</strong> {q.vehicleDetails?.fuelType}</p>
                            <p><strong>Cubic Capacity:</strong> {q.vehicleDetails?.cubicCapacity}</p>
                            <p><strong>Seating Capacity:</strong> {q.vehicleDetails?.seatingCapacity}</p>
                            <p><strong>Registration Year:</strong> {q.vehicleDetails?.registrationYear}</p>
                            <p><strong>Policy Type:</strong> {q.vehicleDetails?.policyType}</p>
                            <p><strong>Policy Expiry:</strong> {q.vehicleDetails?.policyExpiryDate}</p>
                            <p><strong>City:</strong> {q.vehicleDetails?.city}</p>
                            <p><strong>Zone:</strong> {q.vehicleDetails?.zone}</p>
                            <p><strong>Vehicle Age:</strong> {q.vehicleDetails?.vehicleAge}</p>
                            <p><strong>Previous Claim %:</strong> {q.vehicleDetails?.previousClaimPercent}</p>
                          </div>
                        </div>

                        {/* IDV DETAILS */}
                        <div className="detail-section">
                          <h3>IDV Details</h3>
                          <div className="detail-grid">
                            <p><strong>Vehicle IDV:</strong> ₹{Number(q.idvDetails?.vehicleIDV || 0).toLocaleString("en-IN")}</p>
                            <p><strong>Electrical:</strong> ₹{Number(q.idvDetails?.electrical || 0).toLocaleString("en-IN")}</p>
                            <p><strong>Non Electrical:</strong> ₹{Number(q.idvDetails?.nonElectrical || 0).toLocaleString("en-IN")}</p>
                            <p><strong>Bi-Fuel:</strong> ₹{Number(q.idvDetails?.biFuel || 0).toLocaleString("en-IN")}</p>
                            <p><strong>Total IDV:</strong> ₹{Number(q.idvDetails?.totalIDV || 0).toLocaleString("en-IN")}</p>
                          </div>
                        </div>

                        <div className="detail-section">
                          <h3>Own Damage Premium (A)</h3>
                          <div className="detail-grid">
                            <p><strong>Basic OD Premium (Vehicle):</strong> ₹{Number(q.premiumDetails?.basicODVehicle || 0)}</p>
<p><strong>Elec. Accessories:</strong> ₹{Number(q.premiumDetails?.elecAccessories || 0)}</p>
<p><strong>Non-Elec Accessories:</strong> ₹{Number(q.premiumDetails?.nonElecAccessories || 0)}</p>
<p><strong>Bi-Fuel Kit:</strong> ₹{Number(q.premiumDetails?.biFuelKit || 0).toLocaleString("en-IN")}</p>
<p><strong>Sub Total Basic Premium:</strong> ₹{Number(q.premiumDetails?.subTotalBasic || 0)}</p>
<p><strong>Anti-Theft Device:</strong> ₹{Number(q.premiumDetails?.discountAntiTheft || 0).toLocaleString("en-IN")}</p>
<p><strong>No Claim Bonus (%):</strong> ₹{Number(q.discounts?.ncbAmount || 0)}</p>
<p><strong>Sub Total (Deductibles):</strong> ₹{Number(q.premiumDetails?.subTotalDiscounts || 0).toLocaleString("en-IN")}</p>
<p><strong>Total OD Premium:</strong> ₹{Number((q.premiumDetails?.subTotalBasic || 0) - (q.discounts?.ncbAmount || 0)).toLocaleString("en-IN")}</p>
<p><strong>Add-on Amount:</strong> ₹{Number(q.addon_amount || 0)}</p>
<p><strong>Net OD Premium (A):</strong> ₹{Number((q.premiumDetails?.subTotalBasic || 0) - (q.discounts?.ncbAmount || 0) + (q.addon_amount || 0)).toLocaleString("en-IN")}</p>
                          </div>
                        </div>

                        <div className="detail-section">
                          <h3>Liability Premium (B)</h3>
                          <div className="detail-grid">
                            <p><strong>Third Party Base Premium:</strong> ₹{Number((() => { const cc = Number((q.vehicleDetails?.cubicCapacity || "").toString().trim()); if (!cc || cc === 0 || isNaN(cc)) return 0; else if (cc <= 1000) return 2094; else if (cc <= 1500) return 3416; else return 7897; })()).toLocaleString("en-IN")}</p>
<p><strong>Bi-Fuel Kit:</strong> ₹{Number(q.liab_bifuel || 0).toLocaleString("en-IN")}</p>
<p><strong>PA Owner Driver:</strong> ₹{Number(q.paOwnerDriver || 0)}</p>
<p><strong>PA Unnamed Passenger:</strong> ₹{Number(q.paUnnamedPassenger || 0)}</p>
<p><strong>PA Paid Driver:</strong> ₹{Number(q.paPaidDriver || 0)}</p>
<p><strong>LL to Paid Driver:</strong> ₹{Number(q.llPaidDriver || 0)}</p>
<p><strong>LL to Employees:</strong> ₹{Number(q.llEmployees || 0)}</p>
<p><strong>Net Liability Premium (B):</strong> ₹{Number(q.net_liability_B || 0)}</p>
                          </div>
                        </div>

                        <div className="detail-section final-summary">
                          <h3>Final Summary</h3>
                          <div className="detail-grid">
                            <p><strong>Total Premium (A+B):</strong> ₹{Number(((q.premiumDetails?.subTotalBasic || 0) - (q.discounts?.ncbAmount || 0) + (q.addon_amount || 0)) + (q.net_liability_B || 0)).toLocaleString("en-IN")}</p>
<p><strong>GST (SGST 9% + CGST 9%):</strong> ₹{Number((q.taxes?.sgst || 0) + (q.taxes?.cgst || 0))}</p>
<p><strong>Gross Premium Paid:</strong> ₹{Number(q.premiumDetails?.grossPremium || 0)}</p>
                          </div>
                        </div>

                        <div className="detail-section">
                          <h3>Add-On Covers</h3>
                          <div className="detail-grid">
                            {q.addOns && Object.entries(q.addOns)
                              .filter(([_, v]) => v?.included)
                              .map(([k, v]) => (
                                <p key={k}><strong>{addOnLabels[k] || k}</strong></p>
                              ))}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div className="modal-footer">
                  <button onClick={() => handleExportPDF(selectedQuotation)} className="btn btn-primary">
                    <FaFilePdf /> Export PDF
                  </button>
                  <button onClick={() => setSelectedQuotation(null)} className="btn btn-secondary">Close</button>
                </div>

              </div>
            </div>
          )}

        </div>
      </div>

    </div>
  );
};

export default QuotationHistory;
